<!--
 * @Author: zcc zcc-2578840576 2578840576@qq.com
 * @Date: 2026-03-27 15:23:20
 * @LastEditors: zcc zcc-2578840576 2578840576@qq.com
 * @LastEditTime: 2026-03-30 17:04:29
 * @FilePath: \hzs-nutra-wecom-shop\src\components\PullAndPush_list\index.vue
 * @Description: 列表组件, 允许下拉刷新，上拉加载
-->

<template>
  <div
    ref="scrollContainer"
    class="pull-and-push-list"
    @touchstart="onTouchStart"
    @touchmove="onTouchMove"
    @touchend="onTouchEnd"
  >
    <div
      class="pull-refresh-indicator"
      :style="{ height: `${pullDistance}px` }"
    >
      <span v-if="isRefreshing">正在刷新...</span>
      <span v-else>{{ canTriggerRefresh ? "松开刷新" : "下拉刷新" }}</span>
    </div>

    <div v-if="pageList.length > 0" class="listBox">
      <slot :list="pageList" />
    </div>

    <div v-else-if="!isLoading && !isRefreshing" class="no-order-container">
      <img
        :src="
          props.emptyImg || comapi.getImageUrl('wxmini/img/orderlist_none.png')
        "
        class="no-order-img"
      />
      <div class="no-order">{{ props.emptyText }}</div>
    </div>

    <div class="bottom-state">
      <div v-if="isLoading" class="loading-text">正在加载中...</div>
      <div v-else-if="isFinished && pageList.length > 0" class="finished-text">
        {{ props.noMoreText }}
      </div>
    </div>

    <div ref="loadTrigger" class="load-trigger"></div>
  </div>
</template>

<script lang="ts" setup>
import comapi from "@/utils/api/com";
import { onMounted, onUnmounted, ref } from "vue";

type QueryParams = {
  page: number;
  page_size: number;
};

type QueryResult<T = any> = {
  list: T[];
  total?: number;
  finished?: boolean;
};

type QueryFn<T = any> = (params: QueryParams) => Promise<QueryResult<T> | T[]>;

// --------------------------------------- 变量
const props = withDefaults(
  defineProps<{
    request: QueryFn;
    otherParams?: () => {} | {};
    page_size?: number;
    immediate?: boolean;
    refreshLoadAll?: boolean;
    emptyImg?: string;
    emptyText?: string;
    noMoreText?: string;
  }>(),
  {
    page_size: 10,
    immediate: true,
    refreshLoadAll: true,
    emptyImg: "",
    emptyText: "暂无数据",
    noMoreText: "没有更多数据了",
  },
);
const emit = defineEmits<{
  (e: "update:list", list: any[]): void;
  (e: "loaded", payload: { page: number; list: any[] }): void;
}>();

// === 分页逻辑变量 ===
const isLoading = ref(false);
const isRefreshing = ref(false);
const isFinished = ref(false);
const page = ref(0);
const pageList = ref<any[]>([]);
const scrollContainer = ref<HTMLElement | null>(null);
const loadTrigger = ref<HTMLElement | null>(null);
const pullDistance = ref(0);
const canTriggerRefresh = ref(false);
const pullThreshold = 70;
let touchStartY = 0;
let observer: IntersectionObserver | null = null;
let basePageSize = props.page_size;
// --------------------------------------- 方法、事件
/**
 * @description: 获取otherParams参数值
 * @method getOtherParams
 * @returns {object} otherParams值
 */
const getOtherParams = () => {
  return props.otherParams || {};
};

/**
 * @description: 更新页面状态并触发事件
 * @method updatePageState
 * @params {any[]} [list] 新的列表数据
 * @params {number} [currentPage] 当前页码
 * @params {boolean} [finished] 是否结束
 * @returns {void}
 */
const updatePageState = (
  list: any[],
  currentPage: number,
  finished: boolean,
) => {
  pageList.value = list;
  page.value = currentPage;
  isFinished.value = finished;
  emit("update:list", [...pageList.value]);
  emit("loaded", { page: page.value, list: [...pageList.value] });
};

/**
 * @description: 统一标准化分页请求返回结果，兼容数组和对象结构
 * @method normalizeResult
 * @params {QueryResult | any[]} [res] 请求返回的原始数据
 * @returns {{ list: any[]; finished: boolean }} 标准化后的列表和结束状态
 */
const normalizeResult = (res: QueryResult | any[]) => {
  // 处理数组类型返回值
  if (Array.isArray(res)) {
    return {
      list: res,
      finished: res.length < props.page_size,
    };
  }

  // 处理对象类型返回值
  try {
    // 从data字段获取数据
    const list = Array.isArray(res.data) ? res.data : [];

    // 计算结束状态，直接使用cnt字段
    const finished = pageList.value.length + list.length >= res.cnt;

    return {
      list,
      finished,
    };
  } catch (err) {
    console.error("分页结果解析失败", err);
    return {
      list: [],
      finished: true,
    };
  }
};

/**
 * @description: 请求指定页码数据并返回标准化结果
 * @method requestOnePage
 * @params {number} [targetPage] 目标页码
 * @returns {Promise<{ list: any[]; finished: boolean }>} 当前页数据与结束状态
 */
const requestOnePage = async (targetPage: number) => {
  const otherParamsValue = getOtherParams();
  const rawRes = await props.request({
    // 注意：otherParams 可能包含 page/page_size（父页面旧状态）。
    // 这里让组件内部的 page/page_size 始终生效，避免被覆盖导致分页一直请求第1页。
    ...otherParamsValue,
    page: targetPage,
    page_size: basePageSize,
  });
  return normalizeResult(rawRes);
};

/**
 * @description: 加载单页数据并合并到组件内部列表
 * @method loadSinglePage
 * @params {number} [targetPage] 要加载的页码
 * @returns {Promise<void>}
 */
const loadSinglePage = async (targetPage: number) => {
  const { list, finished } = await requestOnePage(targetPage);
  if (targetPage === 1) {
    updatePageState(list, targetPage, finished);
  } else {
    const newList = [...pageList.value, ...list];
    updatePageState(newList, targetPage, finished);
  }
};

/**
 * @description: 按当前页码一次性回刷累计数据（如第5页则 page=1,page_size=5*size）
 * @method reloadAccumulated
 * @params {void} [none] 无参数
 * @returns {Promise<void>}
 */
const reloadAccumulated = async () => {
  const currentPage = Math.max(page.value, 1);
  const expandedPageSize = currentPage * basePageSize;
  const otherParamsValue = getOtherParams();
  const rawRes = await props.request({
    // 同样让组件控制的回刷参数覆盖 otherParams 中的 page/page_size
    ...otherParamsValue,
    page: 1,
    page_size: expandedPageSize,
  });
  const { list, finished } = normalizeResult(rawRes);
  updatePageState(list, currentPage, finished);
};

/**
 * @description: 触发上拉加载，加载下一页数据
 * @method handleLoadMore
 * @params {void} [none] 无参数
 * @returns {Promise<void>}
 */
const handleLoadMore = async () => {
  if (isRefreshing.value || isFinished.value || isLoading.value) return;
  isLoading.value = true;
  try {
    const targetPage = page.value + 1;
    await loadSinglePage(targetPage);
  } catch (err) {
    console.error("上拉加载失败", err);
  } finally {
    isLoading.value = false;
  }
};

/**
 * @description: 触发下拉刷新，支持回刷累计页数据
 * @method handleRefresh
 * @params {void} [none] 无参数
 * @returns {Promise<void>}
 */
const handleRefresh = async () => {
  if (isLoading.value) return;
  isRefreshing.value = true;
  try {
    if (props.refreshLoadAll && page.value > 1) {
      await reloadAccumulated();
    } else {
      await loadSinglePage(1);
    }
  } catch (err) {
    console.error("下拉刷新失败", err);
  } finally {
    isRefreshing.value = false;
  }
};

/**
 * @description: 记录触摸起点，仅在滚动容器位于顶部时生效
 * @method onTouchStart
 * @params {TouchEvent} [event] 触摸开始事件对象
 * @returns {void}
 */
const onTouchStart = (event: TouchEvent) => {
  if (!scrollContainer.value) return;
  if (scrollContainer.value.scrollTop > 0) return;
  touchStartY = event.touches[0].clientY;
  canTriggerRefresh.value = false;
};

/**
 * @description: 计算下拉位移与刷新阈值状态
 * @method onTouchMove
 * @params {TouchEvent} [event] 触摸移动事件对象
 * @returns {void}
 */
const onTouchMove = (event: TouchEvent) => {
  if (!scrollContainer.value || isRefreshing.value || isLoading.value) return;
  if (scrollContainer.value.scrollTop > 0) return;
  const diff = event.touches[0].clientY - touchStartY;
  if (diff <= 0) {
    pullDistance.value = 0;
    canTriggerRefresh.value = false;
    return;
  }
  pullDistance.value = Math.min(diff * 0.5, 120);
  canTriggerRefresh.value = pullDistance.value >= pullThreshold;
};

/**
 * @description: 触摸结束时判断是否触发刷新并重置下拉状态
 * @method onTouchEnd
 * @params {void} [none] 无参数
 * @returns {Promise<void>}
 */
const onTouchEnd = async () => {
  if (canTriggerRefresh.value) {
    pullDistance.value = pullThreshold;
    await handleRefresh();
  }
  pullDistance.value = 0;
  canTriggerRefresh.value = false;
};

/**
 * @description: 重置分页状态与列表数据
 * @method reset
 * @params {void} [none] 无参数
 * @returns {void}
 */
const reset = () => {
  page.value = 0;
  pageList.value = [];
  isFinished.value = false;
  emit("update:list", []);
  emit("loaded", { page: 0, list: [] });
};

/**
 * @description: 对外暴露的刷新方法，触发一次下拉刷新逻辑
 * @method refresh
 * @params {void} [none] 无参数
 * @returns {Promise<void>}
 */
const refresh = async () => {
  await handleRefresh();
};

const reloadCurrentPage = async () => {
  if (page.value === 0) {
    await loadSinglePage(1);
  } else {
    await loadSinglePage(page.value);
  }
};

defineExpose({
  reset,
  refresh,
  loadMore: handleLoadMore,
  reloadCurrentPage,
  page,
  list: pageList,
});

onMounted(async () => {
  basePageSize = props.page_size;
  observer = new IntersectionObserver(
    (entries) => {
      if (entries[0]?.isIntersecting) {
        handleLoadMore();
      }
    },
    {
      threshold: 0.1,
      root: scrollContainer.value,
      rootMargin: "50px",
    },
  );
  if (loadTrigger.value) {
    observer.observe(loadTrigger.value);
  }

  if (!props.immediate) return;
  await handleLoadMore();
});

onUnmounted(() => {
  if (observer) {
    observer.disconnect();
    observer = null;
  }
});
</script>

<style lang="scss" scoped>
.pull-and-push-list {
  height: 100%;
  overflow: auto;
  -webkit-overflow-scrolling: touch;
}

.pull-refresh-indicator {
  display: flex;
  align-items: center;
  justify-content: center;
  color: #999;
  font-size: 13px;
  transition: height 0.2s ease;
  overflow: hidden;
}

.bottom-state {
  min-height: 34px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.loading-text,
.finished-text {
  color: #999;
  font-size: 13px;
}

.load-trigger {
  height: 1px;
}

// 空状态
.no-order-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  margin-top: 100px;

  .no-order-img {
    width: 142px;
    height: 121px;
    margin-bottom: 10px;
  }

  .no-order {
    font-size: 15px;
    color: #666;
  }
}
</style>
